<template>
  <v-card class="pa-3 mb-4">
    <v-row style="max-width: 100%; overflow-x: auto; display: flex; flex-wrap: nowrap">
      <v-col v-if="locationBasedInventoryEnabled" cols="auto">
        <product-location-filter />
      </v-col>
      <v-col cols="auto">
        <name-filter context="product" />
      </v-col>
      <!-- Fuzzy search not available in firebase; This query not supported -->
      <!--      <v-col cols="auto">-->
      <!--        <product-description-filter />-->
      <!--      </v-col>-->
      <v-col cols="auto">
        <product-group-filter />
      </v-col>
      <v-col cols="auto">
        <tag-filter />
      </v-col>
      <v-col cols="auto">
        <product-status-filter />
      </v-col>
      <v-col cols="auto">
        <product-stock-filter />
      </v-col>
      <v-col class="d-flex justify-end">
        <v-btn
          v-if="Object.keys(filters)?.length > 0"
          variant="text"
          color="primary"
          class="mr-3"
          @click="resetFilters()"
        >
          {{ $t("page.payment.filter.clearFilters") }}
        </v-btn>
      </v-col>
    </v-row>
  </v-card>
</template>

<script lang="ts" setup>
/**
 * ProductSearchComponent
 *
 * This component handles filters for product search. When used on the product list page,
 * it supports persisting filter state using the Pages.PRODUCTS route name.
 *
 * The resetFilters method checks the current route and:
 * - If on the products page, clears filters using the clearRouteFilters method
 * - For other pages, uses the traditional approach of manually clearing specific filter properties
 */

import { computed } from "vue";
import { useProductsStore } from "@/catalog/stores/products";
import ProductStatusFilter from "@/catalog/components/product/ProductFilter/ProductStatusFilter.vue";
import NameFilter from "@/shared/components/Common/SearchFilter/NameFilter.vue";
import ProductStockFilter from "@/catalog/components/product/ProductFilter/ProductStockFilter.vue";
import ProductGroupFilter from "@/shared/components/Common/SearchFilter/ProductGroupFilter.vue";
import TagFilter from "@/shared/components/Common/SearchFilter/TagFilter.vue";
import ProductLocationFilter from "@/catalog/components/product/ProductFilter/ProductLocationFilter.vue";
import { useRoute } from "vue-router";
import { Pages } from "@/shared/enums/route.enum";
import { useAccountStore } from "@/account/stores/account";

const productsStore = useProductsStore();
const { setFilterProductCondition } = productsStore;
const route = useRoute();
const accountStore = useAccountStore();

const filters = computed(() => productsStore.filterProductCondition || null);

const locationBasedInventoryEnabled = computed(() => {
  const account = accountStore.account;
  return account?.accountSettings?.inventory?.location_based_inventory_enabled === true;
});

const resetFilters = async () => {
  // Check if we're on the products page
  const isProductsRoute = route.name === Pages.PRODUCTS;

  if (isProductsRoute) {
    const { sortBy, sortDirection } = productsStore.filterProductCondition;
    // Use the clearRouteFilters method for the products route
    productsStore.clearRouteFilters(Pages.PRODUCTS);
    productsStore.setFilterProductCondition({
      active: true,
      ...(sortBy ? { sortBy } : {}),
      ...(sortDirection ? { sortDirection } : {}),
    });
  } else {
    // For other routes, use the traditional approach
    const filterProductCondition = productsStore.filterProductCondition;
    delete filterProductCondition.name;
    //delete filterProductCondition.description;
    filterProductCondition.active = true;
    delete filterProductCondition.lowStock;
    delete filterProductCondition.outOfStock;
    delete filterProductCondition.stockMin;
    delete filterProductCondition.stockMax;
    delete filterProductCondition.productId; // Added to clear the catalog filter
    delete filterProductCondition.productClassId; // Added to clear the product class filter
    delete filterProductCondition.tags; // Add this line to clear the tags filter
    delete filterProductCondition.locationId;
    setFilterProductCondition(filterProductCondition);
  }

  // The listLoading flag will be set by the store method
  // Explicitly set initLoading=false to only show loading in the table
  await productsStore.searchProducts({}, false);
};
</script>
